import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { setAuthTokens, clearAuthTokens, getAccessToken } from '../lib/authTokens';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // No token cached → skip the API roundtrip and treat as logged out
      if (!getAccessToken()) {
        setUser(false);
        return;
      }
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (error) {
      // Token expired or invalid → clear and treat as logged out
      clearAuthTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Store the bearer token alongside the cookie. The bearer token survives
      // cloud ingress quirks that sometimes drop cookies.
      setAuthTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      setUser({ email: data.email, name: data.name, role: data.role });
      toast.success('Logged in successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(errorMessage);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Logout failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      // Always clear local state — even if API call failed
      clearAuthTokens();
      setUser(false);
      toast.success('Logged out successfully');
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, logout, checkAuth }),
    [user, loading, login, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
